import React, { Children } from 'react';
import classnames from 'classnames';
import { CSSTransition } from 'react-transition-group';

import { isElementOfType } from '../../utilities/components';

import { Width } from './types';
import WpPageHeader from './WpPageHeader';
import WpPageBody from './WpPageBody';
import WpPageUtilityNavBar from './WpPageUtilityNavBar';
import WpPageSideRail from './WpPageSideRail';

import './WpPageLayout.scss';

export const DEFAULT_WIDTH = 'full';

export interface Props {
  children?: React.ReactNode;
  width?: Width;
  className?: string;
  pageRef?: React.Ref<HTMLDivElement>;
  isSideRailOpen?: boolean;
  toggleIsSideRailOpen?: () => void;
}

const WpPageLayout: React.FC<Props> = ({
  children,
  className = '',
  width = DEFAULT_WIDTH,
  pageRef,
  isSideRailOpen = true,
  toggleIsSideRailOpen,
}: Props) => {
  const baseClassName = 'WpPageLayout';

  let hasSideRail = false;
  let sideRailComponent = null;
  Children.forEach(children, child => {
    if (isElementOfType(child, [WpPageSideRail])) {
      hasSideRail = true;
      sideRailComponent = child;
    }
  });

  const classes = classnames(baseClassName, `${baseClassName}--${width}`, className);

  const mainContentClass = classnames(`${baseClassName}__wrapper__main`, {
    [`${baseClassName}__wrapper__main--with-side-rail`]: hasSideRail && isSideRailOpen,
  });

  const mainContent = React.Children.map(children, (child: React.ReactElement<any>) => {
    if (isElementOfType(child, [WpPageSideRail])) {
      return null;
    }

    if (child && isElementOfType(child, [WpPageHeader, WpPageBody, WpPageUtilityNavBar])) {
      return React.cloneElement(child, { width: child.props?.width || width });
    }

    return child;
  });

  return (
    <div className={classes} ref={pageRef}>
      {hasSideRail ? (
        <div className={`${baseClassName}__wrapper`}>
          <div className={mainContentClass}>{mainContent}</div>
          <CSSTransition
            in={isSideRailOpen}
            timeout={350}
            classNames={`${baseClassName}__wrapper__side-rail__animation`}
            unmountOnExit
          >
            <div className={`${baseClassName}__wrapper__side-rail`}>
              {React.cloneElement(sideRailComponent, { ...sideRailComponent.props, toggleIsSideRailOpen })}
            </div>
          </CSSTransition>
        </div>
      ) : (
        <>{mainContent}</>
      )}
    </div>
  );
};

export default WpPageLayout;
